/**
 * Pointy-top axial coordinates and edge indexing per
 * docs/task-log/20260505-cascadia-online/02-rules-spec.md §4.
 *
 * The board is open (no fixed lattice). Cells are stored sparsely keyed by
 * `coordKey()` strings.
 */

export interface AxialCoord {
  readonly q: number;
  readonly r: number;
}

export type EdgeIndex = 0 | 1 | 2 | 3 | 4 | 5;

export interface NeighbourOffset {
  readonly edge: EdgeIndex;
  readonly dq: number;
  readonly dr: number;
  readonly reciprocal: EdgeIndex;
}

export const NEIGHBOUR_OFFSETS: readonly NeighbourOffset[] = [
  { edge: 0, dq: 1, dr: -1, reciprocal: 3 },
  { edge: 1, dq: 1, dr: 0, reciprocal: 4 },
  { edge: 2, dq: 0, dr: 1, reciprocal: 5 },
  { edge: 3, dq: -1, dr: 1, reciprocal: 0 },
  { edge: 4, dq: -1, dr: 0, reciprocal: 1 },
  { edge: 5, dq: 0, dr: -1, reciprocal: 2 },
];

export const coordKey = (c: AxialCoord): string => `${c.q},${c.r}`;

export const parseCoordKey = (key: string): AxialCoord => {
  const parts = key.split(",");
  if (parts.length !== 2) {
    throw new Error(`Invalid coord key: ${key}`);
  }
  const [qStr, rStr] = parts;
  if (qStr === "" || rStr === "" || qStr === undefined || rStr === undefined) {
    throw new Error(`Invalid coord key: ${key}`);
  }
  const q = Number(qStr);
  const r = Number(rStr);
  if (!Number.isFinite(q) || !Number.isFinite(r)) {
    throw new Error(`Invalid coord key: ${key}`);
  }
  return { q, r };
};

export const coordEquals = (a: AxialCoord, b: AxialCoord): boolean =>
  a.q === b.q && a.r === b.r;
